import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import i18next from 'i18next';
import _ from 'lodash';
import {
  RulesContext,
  TargetFieldCategory,
  getAvailableTargetFieldNatureOptions,
  getFlatSubFieldsOptions,
  getOrderedOptions,
  getTargetFieldCategoryOptions,
  getTargetFieldOptionsForDocument,
  getTargetFieldOptionsForDossier,
  getTargetFieldOptionsForThirdParty,
  getTargetFieldOptionsForContextModels,
  TargetFieldNature,
  getTargetFieldOptionsForContextDirectField,
  RuleType,
} from 'services/common/RulesUtils';
import useStyles from '../../../../../style/js-style/containers/administration/rules/RuleFormsStyle';
import RuleSelectInput from 'components/rules/RuleSelectInput';
import { CriterionContext } from './CriterionFormFields';
import { isSealedDocument, isSealedDossier } from 'services/common/Constants';
import { hasOption } from 'services/common/Utils';
import { Context } from 'states/Store';
import { 
  ContextDirectField, 
  isMatchingDossierTarget, 
  isMatchingThirdPartyTarget 
} from '../../../../../services/common/CalculatorUtils';



export const TargetFieldContext = React.createContext({
  selectedTargetFieldCategory: null,
});

const TargetField = ({ fieldName, readOnly }) => {
  const { classes } = useStyles();

  const formMethods = useFormContext();
  const { setValue, clearErrors } = formMethods;

  const ruleType = useWatch({ name: 'type' });

  const targetFieldCategoryOptions = getTargetFieldCategoryOptions(ruleType);

  const selectedTargetFieldCategory = useWatch({
    name: `${fieldName}.target_field_category`,
  });

  const onTargetFieldCategoryChange = useCallback(
    (newValue) => {
      setValue(`${fieldName}.target_field_data`, {});
      clearErrors(`${fieldName}.target_field_data`);
    },
    [fieldName, setValue, clearErrors],
  );

  const targetFieldcontext = React.useMemo(
    () => ({
      selectedTargetFieldCategory: selectedTargetFieldCategory,
      readOnly: readOnly,
      ruleType: ruleType
    }),
    [selectedTargetFieldCategory, readOnly, ruleType],
  );

  return (
    <>
      <div className={classes.flexItem}>
        <RuleSelectInput
          fieldName={`${fieldName}.target_field_category`}
          label={i18next.t(
            `rules.configuration.criteria.fields.category.label`,
          )}
          isRequired
          placeholder={i18next.t(
            `rules.configuration.criteria.fields.category.placeholder`,
          )}
          options={targetFieldCategoryOptions}
          onChange={(newValue) => {
            onTargetFieldCategoryChange(newValue);
          }}
          validate={() => {
            return null;
          }}
          variant="standard"
          labelVariant="standard"
          disabled={readOnly}
        />
      </div>

      <TargetFieldContext.Provider value={targetFieldcontext}>
        <TargetFieldData fieldName={`${fieldName}.target_field_data`} />
      </TargetFieldContext.Provider>
    </>
  );
};

const TargetFieldData = ({ fieldName }) => {
  const { selectedTargetFieldCategory } = useContext(TargetFieldContext);

  switch (selectedTargetFieldCategory) {
    case TargetFieldCategory.THIRD_PARTY:
      return (
        <TargetFieldDataForThirdParty
          fieldName={`${fieldName}.data_for_third_party`}
        />
      );

    case TargetFieldCategory.DOSSIER:
      return (
        <TargetFieldDataForDossier
          fieldName={`${fieldName}.data_for_dossier`}
        />
      );

    case TargetFieldCategory.DOCUMENT:
      return (
        <TargetFieldDataForDocument
          fieldName={`${fieldName}.data_for_document`}
        />
      );

    case TargetFieldCategory.CONTEXT:
      return (
        <TargetFieldDataForContext
          fieldName={`${fieldName}.data_for_context`}
        />
      );

    default:
      return <></>;
  }
};

const TargetFieldDataForContext = ({ fieldName }) => {
  const selectedContextModel = useWatch({
    name: fieldName,
  });

  return (<>
    <ContextModels fieldName={fieldName} />
    {selectedContextModel ? (
      <TargetFieldContextTypeField fieldName={fieldName} />
    ) : (
      <></>
    )}
    <InnerTargetField fieldName={fieldName} />
  </>);
};

const ContextModels = ({ fieldName }) => {
  const { classes } = useStyles();
  const { contextModels } = useContext(RulesContext);
  const formMethods = useFormContext();
  const { setValue, clearErrors } = formMethods;

  const onTargetFieldContextModelChange = useCallback(
    (newValue) => {
      setValue(`${fieldName}.field`, '');
      clearErrors(`${fieldName}.field`, '');

      setValue(`${fieldName}.intermediatefield-0`, undefined);
      clearErrors(`${fieldName}.intermediatefield-0`, '');

      setValue(`${fieldName}.field_type`, '');
      clearErrors(`${fieldName}.field_type`, '');
    },
    [fieldName, setValue, clearErrors],
  );

  const parsedModels = getOrderedOptions(contextModels.map((model) => {
    return {
      label: model.name,
      value: model.id,
    };
  }));

  return (
    <>
      <div className={classes.conditionDivider}>/</div>
      {!_.isEmpty(contextModels) && (<div className={classes.flexItem}>
        <RuleSelectInput
          fieldName={`${fieldName}.context_model_id`}
          label={i18next.t(
            `rules.configuration.criteria.fields.model.label`,
          )}
          isRequired
          placeholder={i18next.t(
            `rules.configuration.criteria.fields.model.placeholder`,
          )}
          options={parsedModels}
          onChange={(newValue) => {
            onTargetFieldContextModelChange(newValue);
          }}
          validate={() => {
            return null;
          }}
          variant="standard"
          labelVariant="chip"
        />
      </div>)}
    </>
  );
};

const TargetFieldContextTypeField = ({ fieldName }) => {
  const { classes } = useStyles();
  const { selectedTargetFieldCategory } = useContext(TargetFieldContext);

  const formMethods = useFormContext();
  const { setValue, clearErrors } = formMethods;

  const [
    availableTargetFieldNatureOptions,
    setAvailableTargetFieldNatureOptions,
  ] = useState();

  useEffect(() => {
    setAvailableTargetFieldNatureOptions(
      getAvailableTargetFieldNatureOptions(
        selectedTargetFieldCategory,
      ),
    );
  }, [selectedTargetFieldCategory]);

  const onTargetFieldNatureChange = useCallback(
    (newValue) => {
      setValue(`${fieldName}.field`, '');
      clearErrors(`${fieldName}.field`, '');

      setValue(`${fieldName}.field_type`, '');
      clearErrors(`${fieldName}.field_type`, '');
    },
    [fieldName, setValue, clearErrors],
  );

  if (!_.isEmpty(availableTargetFieldNatureOptions)) {
    return <>
      <div className={classes.conditionDivider}> /</div>
      <div className={classes.flexItem}>
        <RuleSelectInput
          fieldName={`${fieldName}.target_field_nature`}
          label={i18next.t(
            `rules.configuration.criteria.fields.nature.label`,
          )}
          isRequired
          placeholder={i18next.t(
            `rules.configuration.criteria.fields.nature.placeholder`,
          )}
          options={getOrderedOptions(availableTargetFieldNatureOptions)}
          onChange={(newValue) => {
            if (ContextDirectField.includes(newValue)) {
              const entry = getTargetFieldOptionsForContextDirectField(newValue);
              setValue(`${fieldName}.field`, entry.value);
              setValue(`${fieldName}.field_type`, entry.type);
              setValue(`${fieldName}.field_sub_type`, entry.subType || ``);
              setValue(`${fieldName}.target_field_nature`, entry.fieldNature);
            } else {
              onTargetFieldNatureChange(newValue);
            }
          }}
          validate={() => {
            return null;
          }}
          variant="standard"
          labelVariant="chip"
        />
      </div>
    </>;
  } else {
    return <></>;
  }
}

const TargetFieldDataForThirdParty = ({ fieldName }) => {
  const { readOnly } = useContext(TargetFieldContext);

  return (
    <>
      <TargetFieldNatureField fieldName={fieldName} readOnly={readOnly} />

      <InnerTargetField fieldName={fieldName} readOnly={readOnly} />
    </>
  );
};

const TargetFieldDataForDocument = ({ fieldName }) => {
  const { readOnly } = useContext(TargetFieldContext);

  return (
    <>
      <TargetFieldDocumentTypeField fieldName={fieldName} readOnly={readOnly} />

      <InnerTargetField fieldName={fieldName} readOnly={readOnly} />
    </>
  );
};

const TargetFieldDocumentTypeField = ({ fieldName }) => {
  const { classes } = useStyles();

  const { documentTypes } = useContext(RulesContext);

  const { selectedTargetFieldCategory, readOnly } = useContext(TargetFieldContext);

  const formMethods = useFormContext();
  const { setValue, clearErrors } = formMethods;

  const onTargetFieldDocumentTypeChange = useCallback(
    (newValue) => {
      setValue(`${fieldName}.field`, '');
      clearErrors(`${fieldName}.field`, '');

      setValue(`${fieldName}.intermediatefield-0`, undefined);
      clearErrors(`${fieldName}.intermediatefield-0`, '');

      setValue(`${fieldName}.field_type`, '');
      clearErrors(`${fieldName}.field_type`, '');
    },
    [fieldName, setValue, clearErrors],
  );

  switch (selectedTargetFieldCategory) {
    case TargetFieldCategory.DOCUMENT:
      return (
        <>
          <div className={classes.conditionDivider}> /</div>
          <div className={classes.flexItem}>
            <RuleSelectInput
              fieldName={`${fieldName}.document_type_id`}
              label={i18next.t(
                `rules.configuration.criteria.fields.nature.label`,
              )}
              isRequired
              placeholder={i18next.t(
                `rules.configuration.criteria.fields.nature.placeholder`,
              )}
              options={getOrderedOptions(
                documentTypes?.filter((x) => !isSealedDocument(x.code))
              )}
              onChange={(newValue) => {
                onTargetFieldDocumentTypeChange(newValue);
              }}
              validate={() => {
                return null;
              }}
              variant="standard"
              labelVariant="chip"
              disabled={readOnly}
            />
          </div>
        </>
      );

    default:
      return <></>;
  }
};

const TargetFieldDataForDossier = ({ fieldName }) => {
  const { readOnly } = useContext(TargetFieldContext);

  return (
    <>
      <TargetFieldDossierTypeField fieldName={fieldName} readOnly={readOnly} />

      <InnerTargetField fieldName={fieldName} readOnly={readOnly} />
    </>
  );
};
const TargetFieldDossierTypeField = ({ fieldName }) => {
  const { classes } = useStyles();

  const { dossierTypes } = useContext(RulesContext);

  const [filteredDossierTypes, setFilteredDossierTypes] = useState(dossierTypes);

  const { selectedTargetFieldCategory, readOnly, ruleType } = useContext(TargetFieldContext);

  useEffect(() => {
    if (RuleType.ON_DOSSIER === ruleType) {
      const filteredDossierTypeList = dossierTypes.filter(dt => !isSealedDossier(dt.code));
      setFilteredDossierTypes(filteredDossierTypeList);
    } else {
      setFilteredDossierTypes(dossierTypes);
    }
  }, [dossierTypes, ruleType]);

  const formMethods = useFormContext();
  const { setValue, clearErrors } = formMethods;

  const onTargetFieldDossierTypeChange = useCallback(() => {
    setValue(`${fieldName}.field`, '');
    clearErrors(`${fieldName}.field`, '');

    setValue(`${fieldName}.intermediatefield-0`, undefined);
    clearErrors(`${fieldName}.intermediatefield-0`, '');

    setValue(`${fieldName}.field_type`, '');
    clearErrors(`${fieldName}.field_type`, '');
  }, [fieldName, setValue, clearErrors]);

  switch (selectedTargetFieldCategory) {
    case TargetFieldCategory.DOSSIER:
      return (
        <>
          <div className={classes.conditionDivider}> /</div>
          <div className={classes.flexItem}>
            <RuleSelectInput
              fieldName={`${fieldName}.dossier_type_id`}
              label={i18next.t(
                `rules.configuration.criteria.fields.nature.label`,
              )}
              isRequired
              placeholder={i18next.t(
                `rules.configuration.criteria.fields.nature.placeholder`,
              )}
              options={filteredDossierTypes}
              onChange={(newValue) => {
                onTargetFieldDossierTypeChange(newValue);
              }}
              validate={() => {
                return null;
              }}
              variant="standard"
              labelVariant="standard"
              disabled={readOnly}
            />
          </div>
        </>
      );

    default:
      return <></>;
  }
};

const TargetFieldNatureField = ({ fieldName }) => {
  const { classes } = useStyles();

  const { selectedTargetFieldCategory, readOnly } = useContext(TargetFieldContext);

  const formMethods = useFormContext();
  const { setValue, clearErrors } = formMethods;

  const [
    availableTargetFieldNatureOptions,
    setAvailableTargetFieldNatureOptions,
  ] = useState();
  const [show, setShow] = useState(false);
  const [state] = useContext(Context);

  const hasOptionSubscription = useCallback(
    (option) => {
      return hasOption(state?.accounts, state?.account?.id, option);
    },
    [state?.account?.id, state?.accounts],
  );

  useEffect(() => {
    setAvailableTargetFieldNatureOptions(
      getAvailableTargetFieldNatureOptions(
        selectedTargetFieldCategory,
        hasOptionSubscription,
      ),
    );
  }, [selectedTargetFieldCategory, hasOptionSubscription]);

  useEffect(() => {
    const bShow =
      !_.isEmpty(availableTargetFieldNatureOptions) &&
      TargetFieldCategory.THIRD_PARTY === selectedTargetFieldCategory;

    setShow(bShow);
  }, [selectedTargetFieldCategory, availableTargetFieldNatureOptions]);

  const onTargetFieldNatureChange = useCallback(
    (newValue) => {
      setValue(`${fieldName}.field`, '');
      clearErrors(`${fieldName}.field`, '');

      setValue(`${fieldName}.field_type`, '');
      clearErrors(`${fieldName}.field_type`, '');
    },
    [fieldName, setValue, clearErrors],
  );

  if (show) {
    return (
      <>
        <div className={classes.conditionDivider}> /</div>
        <div className={classes.flexItem}>
          <RuleSelectInput
            fieldName={`${fieldName}.target_field_nature`}
            label={i18next.t(
              `rules.configuration.criteria.fields.nature.label`,
            )}
            isRequired
            placeholder={i18next.t(
              `rules.configuration.criteria.fields.nature.placeholder`,
            )}
            options={getOrderedOptions(availableTargetFieldNatureOptions)}
            onChange={(newValue) => {
              onTargetFieldNatureChange(newValue);
            }}
            validate={() => {
              return null;
            }}
            variant="standard"
            labelVariant="chip"
            disabled={readOnly}
          />
        </div>
      </>
    );
  } else {
    return <></>;
  }
};

const InnerTargetField = ({ fieldName }) => {
  const { classes } = useStyles();
  const { selectedTargetFieldCategory, readOnly } = useContext(TargetFieldContext);

  const { handleTargetFieldChange } = useContext(CriterionContext);

  const {
    activities,
    countries,
    thirdpartyAttributes,
    dossierAttributes,
    calculatedAttributes,
    thirdPartyIndicators,
    sourceData,
    financialStrengthScores,
    financialStrengthRatings,
    showInduedScores,
    showTransparencyScore,
    showSourceData,
    showFinancialStrengthScores,
    showEsgScores,
    enableTransparencyScore,
    documentTypes,
    showEcovadisScores,
    showSecurityRatingScores,
    showCreditsafeScores,
    lang,
    contextModels
  } = useContext(RulesContext);

  const formMethods = useFormContext();
  const { setValue, getValues } = formMethods;

  const targetFieldData = useWatch({ name: `${fieldName}` });

  const getAvailableTargetFields = useCallback(() => {
    let availableTargetFields = [];

    switch (selectedTargetFieldCategory) {
      case TargetFieldCategory.THIRD_PARTY:
        availableTargetFields = _.filter(
          getTargetFieldOptionsForThirdParty({
            countries: countries,
            activities: activities,
            thirdpartyAttributes: thirdpartyAttributes,
            calculatedAttributes: _.filter(calculatedAttributes, item => isMatchingThirdPartyTarget(item)),
            indicators: _.filter(thirdPartyIndicators, item => isMatchingThirdPartyTarget(item)),
            sourceData: sourceData,
            financialStrengthScores: financialStrengthScores,
            financialStrengthRatings: financialStrengthRatings,
            withInduedScores: showInduedScores,
            withTransparencyScore: showTransparencyScore,
            withSourceData: showSourceData,
            withFinancialStrengthScores: showFinancialStrengthScores,
            withEsgScores: showEsgScores,
            enableTransparencyScore: enableTransparencyScore,
            withEcovadisScores: showEcovadisScores,
            withSecurityRatingScores: showSecurityRatingScores,
            withCreditsafeScores: showCreditsafeScores,
            lang: lang,
          }),
          { fieldNature: targetFieldData?.target_field_nature },
        );
        break;

      case TargetFieldCategory.DOSSIER:
        availableTargetFields = getTargetFieldOptionsForDossier(
          dossierAttributes,
          _.filter(calculatedAttributes, item => isMatchingDossierTarget(item, targetFieldData?.dossier_type_id)),
          _.filter(thirdPartyIndicators, item => isMatchingDossierTarget(item, targetFieldData?.dossier_type_id))
        );
        break;

      case TargetFieldCategory.DOCUMENT:
        availableTargetFields = getTargetFieldOptionsForDocument(
          _.find(documentTypes, { value: targetFieldData?.document_type_id })
            ?.properties || [],
        );
        break;

      case TargetFieldCategory.CONTEXT:
        if (targetFieldData && targetFieldData.target_field_nature !== TargetFieldNature.CONTEXT_RANK) {
          availableTargetFields = getTargetFieldOptionsForContextModels(
            contextModels,
            targetFieldData
          );
        } else if (targetFieldData) {
          availableTargetFields = [getTargetFieldOptionsForContextDirectField(targetFieldData.target_field_nature)];
        }
        break;

      default:
        break;
    }

    return availableTargetFields;
  }, [
    selectedTargetFieldCategory,
    targetFieldData,
    countries,
    activities,
    thirdpartyAttributes,
    calculatedAttributes,
    thirdPartyIndicators,
    sourceData,
    financialStrengthScores,
    financialStrengthRatings,
    showInduedScores,
    showTransparencyScore,
    showSourceData,
    showFinancialStrengthScores,
    showEsgScores,
    enableTransparencyScore,
    documentTypes,
    dossierAttributes,
    showEcovadisScores,
    showSecurityRatingScores,
    showCreditsafeScores,
    lang,
    contextModels
  ]);

  const [availableFields, setAvailableFields] = useState(
    getAvailableTargetFields(),
  );
  const [show, setShow] = useState(false);

  const [availableSubFields, setAvailableSubFields] = useState();
  const [
    availableFieldsWithSubFields,
    setAvailableFieldsWithSubFields,
  ] = useState(getFlatSubFieldsOptions(getAvailableTargetFields()));

  const getDefaultTargetFieldForExistingRules = useCallback(
    (fieldValue) => {
      let entryFinalField = _.find(availableFields, { value: fieldValue });
      let entryFinalFieldWithSubFields = _.find(availableFieldsWithSubFields, {
        value: fieldValue,
      });
      if (entryFinalField) {
        setValue(`${fieldName}.intermediatefield-0`, entryFinalField.value);
        setValue(`${fieldName}.field`, entryFinalField.value);
      } else if (entryFinalFieldWithSubFields?.path) {
        for (var i = 0; i < entryFinalFieldWithSubFields.path.length; i++) {
          setValue(
            `${fieldName}.intermediatefield-${i}`,
            entryFinalFieldWithSubFields.path[i],
          );
          if (i === 0) {
            let entryFirstSubField = _.find(availableFieldsWithSubFields, {
              value: entryFinalFieldWithSubFields.path[i],
            });
            setAvailableSubFields(
              entryFirstSubField.subFields
                ? entryFirstSubField.subFields
                : undefined,
            );
          }
        }
        setValue(`${fieldName}.field`, entryFinalFieldWithSubFields.value);
      }
    },
    [availableFields, availableFieldsWithSubFields, fieldName, setValue],
  );

  useEffect(() => {
    setAvailableFields(getAvailableTargetFields());
    setAvailableFieldsWithSubFields(
      getFlatSubFieldsOptions(getAvailableTargetFields()),
    );
  }, [getAvailableTargetFields]);

  const clearSubFieldsValues = useCallback(
    (initLevel = 1, lastLevel = 10) => {
      for (let level = initLevel; level < lastLevel; level++) {
        setValue(`${fieldName}.intermediatefield-${level}`, undefined);
      }
    },
    [fieldName, setValue],
  );

  useEffect(() => {
    const fieldValue = getValues(`${fieldName}.field`);
    const firsIntermediateValue = getValues(`${fieldName}.intermediatefield-0`);

    //Dans le cas de la modification d'une règle, si le champs intermediatefield-0 n'a pas encore été rempli,
    // il faut le remplir ainsi que les différents niveaux de intermediatefields
    if (fieldValue && !firsIntermediateValue) {
      getDefaultTargetFieldForExistingRules(fieldValue);
    }

    let entryFirstIntermerdiateField = _.find(availableFields, {
      value: getValues(`${fieldName}.intermediatefield-0`),
    });
    // Cas où il y aurait des intermediatefield-0 mais qui ne sont pas compris dans les availableFields
    // Il faut effacer les valeurs de intermediatefield-0 et +
    if (firsIntermediateValue && !entryFirstIntermerdiateField) {
      setValue(`${fieldName}.intermediatefield-0`, undefined);
      setAvailableSubFields(undefined);
      clearSubFieldsValues();
    }
  }, [
    availableFields,
    clearSubFieldsValues,
    fieldName,
    getDefaultTargetFieldForExistingRules,
    getValues,
    setValue,
  ]);

  const onFieldChange = useCallback(
    (newValue) => {
      let entry = _.find(availableFields, { value: newValue });

      if (!entry.subFields) {
        setValue(`${fieldName}.field`, entry.value);
        setValue(`${fieldName}.field_type`, entry.type);
        setValue(`${fieldName}.field_sub_type`, entry.subType || ``);
        setValue(`${fieldName}.target_field_nature`, entry.fieldNature);
        clearSubFieldsValues();
      } else {
        setValue(`${fieldName}.field_type`, undefined);
        setValue(`${fieldName}.field`, undefined);
      }
      setAvailableSubFields(entry.subFields ? entry.subFields : undefined);

      handleTargetFieldChange(newValue);
    },
    [
      availableFields,
      handleTargetFieldChange,
      setValue,
      fieldName,
      clearSubFieldsValues,
    ],
  );

  useEffect(() => {
    let bShow = false;

    switch (selectedTargetFieldCategory) {
      case TargetFieldCategory.THIRD_PARTY:
        bShow = !!targetFieldData?.target_field_nature;
        break;

      case TargetFieldCategory.DOSSIER:
        bShow = !!targetFieldData?.dossier_type_id;
        break;

      case TargetFieldCategory.DOCUMENT:
        bShow = !!targetFieldData?.document_type_id;
        break;

      case TargetFieldCategory.CONTEXT:
        bShow = !!targetFieldData?.target_field_nature && targetFieldData?.target_field_nature !== TargetFieldNature.CONTEXT_RANK;
        break;

      default:
        break;
    }

    setShow(bShow);
  }, [selectedTargetFieldCategory, targetFieldData]);

  if (show) {
    return (
      <>
        <div className={classes.conditionDivider}> /</div>
        <div className={classes.flexItem}>
          <RuleSelectInput
            fieldName={`${fieldName}.intermediatefield-0`}
            label={i18next.t(`rules.configuration.criteria.fields.field.label`)}
            isRequired
            placeholder={i18next.t(
              `rules.configuration.criteria.fields.field.placeholder`,
            )}
            options={getOrderedOptions(availableFields)}
            onChange={(newValue) => {
              onFieldChange(newValue);
            }}
            validate={() => {
              return null;
            }}
            variant="standard"
            labelVariant="chip"
            disabled={readOnly}
          />
        </div>
        {availableSubFields && (
          <DynamicSubTargetField
            fieldName={`${fieldName}`}
            availableFields={availableSubFields}
            level={1}
            readOnly={readOnly}
          />
        )}
      </>
    );
  } else {
    return <></>;
  }
};

const DynamicSubTargetField = ({ fieldName, availableFields, level, readOnly = false }) => {
  const { classes } = useStyles();

  const { handleTargetFieldChange } = useContext(CriterionContext);

  const formMethods = useFormContext();
  const { setValue, getValues } = formMethods;

  const [availableSubFields, setAvailableSubFields] = useState();

  const clearSubFieldsValues = useCallback(
    (initLevel = 1, lastLevel = 10) => {
      for (let level = initLevel; level < lastLevel; level++) {
        setValue(`${fieldName}.intermediatefield-${level}`, undefined);
      }
    },
    [fieldName, setValue],
  );

  useEffect(() => {
    //in case we show intermediate field to define the variable, we remove preexisting value if not in the availableFields
    let entry = _.find(availableFields, {
      value: getValues(`${fieldName}.intermediatefield-${level}`),
    });
    if (!entry) {
      setValue(`${fieldName}.intermediatefield-${level}`, undefined);
      setAvailableSubFields(undefined);
    } else if (entry && !availableSubFields) {
      //if intermediatefield is set but not availableSubFields
      setAvailableSubFields(entry.subFields ? entry.subFields : undefined);
    }
  }, [
    availableFields,
    availableSubFields,
    fieldName,
    getValues,
    level,
    setValue,
  ]);

  const onFieldChange = useCallback(
    (newValue) => {
      let entry = _.find(availableFields, { value: newValue });
      if (!entry.subFields) {
        setValue(`${fieldName}.field`, entry.value);
        setValue(`${fieldName}.field_type`, entry.type);
        setValue(`${fieldName}.field_sub_type`, entry.subType || ``);
        setValue(`${fieldName}.target_field_nature`, entry.fieldNature);
      } else {
        setValue(`${fieldName}.field_type`, undefined);
        setValue(`${fieldName}.field`, undefined);
        clearSubFieldsValues(level + 1);
      }
      handleTargetFieldChange(newValue);
      setAvailableSubFields(entry.subFields ? entry.subFields : undefined);
    },
    [
      availableFields,
      handleTargetFieldChange,
      setValue,
      fieldName,
      clearSubFieldsValues,
      level,
    ],
  );

  return (
    <>
      <div className={classes.conditionDivider}> /</div>
      <div className={classes.flexItem}>
        <RuleSelectInput
          fieldName={`${fieldName}.intermediatefield-${level}`}
          label={``}
          isRequired
          placeholder={i18next.t(
            `rules.configuration.criteria.fields.field.placeholder`,
          )}
          options={availableFields}
          onChange={(newValue) => {
            onFieldChange(newValue);
          }}
          validate={() => {
            return null;
          }}
          variant="standard"
          labelVariant="chip"
          disabled={readOnly}
        />
      </div>
      {availableSubFields && (
        <DynamicSubTargetField
          fieldName={`${fieldName}`}
          availableFields={availableSubFields}
          level={level + 1}
          readOnly={readOnly}
        />
      )}
    </>
  );
};

export default TargetField;
